import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AdminService from "../services/AdminService";
import "../styles/AdminAds.css";
import {
    FiSearch, FiFilter, FiCheckCircle, FiXCircle,
    FiTrash2, FiEye, FiClock, FiAlertCircle,
    FiChevronLeft, FiChevronRight, FiDownload, FiMessageSquare, FiUser,
    FiEdit2, FiTrendingUp, FiCheck, FiX, FiLayers, FiPlus, FiInfo
} from "react-icons/fi";

const STATUS_OPTIONS = [
    { value: "ALL", label: "All Status" },
    { value: "PENDING", label: "Pending Review" },
    { value: "ACCEPTED", label: "Accepted" }
];

const SkeletonRow = () => (
    <div className="skeleton-row">
        <div style={{ width: '60px' }}>
            <div className="skeleton-cell" style={{ width: '50px', height: '50px', borderRadius: '10px' }}></div>
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div className="skeleton-cell" style={{ width: '80%' }}></div>
            <div className="skeleton-cell" style={{ width: '40%' }}></div>
        </div>
        <div className="skeleton-cell" style={{ width: '100px' }}></div>
        <div className="skeleton-cell" style={{ width: '100px' }}></div>
        <div className="skeleton-cell" style={{ width: '80px' }}></div>
        <div className="skeleton-cell" style={{ width: '80px' }}></div>
        <div className="skeleton-cell" style={{ width: '120px' }}></div>
    </div>
);

const AdvancedPagination = ({ current, total, onPageChange }) => {
    if (total <= 1) return null;

    const pages = [];
    const maxVisible = 3;

    // Logic for 1 2 3 ... Next
    for (let i = 0; i < Math.min(maxVisible, total); i++) {
        pages.push(i);
    }

    return (
        <div className="pagination-numbers">
            {pages.map(p => (
                <button
                    key={p}
                    className={`pag-btn ${current === p ? 'active' : ''}`}
                    onClick={() => onPageChange(p)}
                >
                    {p + 1}
                </button>
            ))}

            {total > maxVisible && (
                <>
                    <span className="pagination-dots">...</span>
                    <button
                        className="pag-btn"
                        onClick={() => onPageChange(Math.min(current + 1, total - 1))}
                        disabled={current === total - 1}
                    >
                        Next <FiChevronRight size={18} />
                    </button>
                </>
            )}
        </div>
    );
};

const AdListPage = () => {
    const navigate = useNavigate();

    // UI State
    const [activeTab, setActiveTab] = useState("ALL"); // ALL | REJECTED
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ total: 0, active: 0, pending: 0, blocked: 0 });

    // All Ads State
    const [ads, setAds] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalItems, setTotalItems] = useState(0);

    // Rejected Ads State
    const [rejectedAds, setRejectedAds] = useState([]);
    const [rejectedPage, setRejectedPage] = useState(0);
    const [rejectedTotalPages, setRejectedTotalPages] = useState(0);
    const [rejectedTotalItems, setRejectedTotalItems] = useState(0);

    // Moderation Modals
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showAcceptModal, setShowAcceptModal] = useState(false);
    const [selectedAd, setSelectedAd] = useState(null);
    const [actionComment, setActionComment] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);

    const getImageUrl = (thumbnail) => {
        if (!thumbnail) return "https://via.placeholder.com/50";
        if (typeof thumbnail === 'string' && thumbnail.startsWith("http")) return thumbnail;
        // Fallback for relative paths if any remain
        return `http://localhost:8080/product/files/${thumbnail}`;
    };

    const formatDate = (dateValue) => {
        if (!dateValue) return "N/A";
        try {
            const date = new Date(dateValue);
            if (isNaN(date.getTime())) return "Invalid Date";
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
        } catch (e) {
            return "Invalid Date";
        }
    };

    const isExpired = (expiryDate) => {
        if (!expiryDate) return false;
        return new Date(expiryDate) < new Date();
    };

    const getStatusBadge = (status) => {
        const s = status || "PENDING";
        return (
            <span className={`status-badge badge-${s.toLowerCase()}`}>
                {s === "ACCEPTED" ? <FiCheckCircle size={12} /> : s === "REJECTED" ? <FiXCircle size={12} /> : <FiClock size={12} />}
                {s === "PENDING" ? "Pending Review" : s.charAt(0) + s.slice(1).toLowerCase()}
            </span>
        );
    };

    const fetchAds = useCallback(async () => {
        if (activeTab === "REJECTED") return;
        setLoading(true);
        try {
            const params = {
                page: currentPage,
                size: 10,
                search: searchTerm || undefined,
                status: statusFilter !== "ALL" ? statusFilter : undefined
            };
            const response = await AdminService.getAds(params);

            if (response.data?.success) {
                const { content, totalPages, totalElements, kpi } = response.data.data;
                setAds(content || []);
                setTotalPages(totalPages || 0);
                setTotalItems(totalElements || 0);
                if (kpi) {
                    setStats({
                        total: kpi.totalAds || kpi.total || 0,
                        active: kpi.liveAds || kpi.active || 0,
                        pending: kpi.pendingReview || kpi.pending || 0,
                        blocked: kpi.rejectedAds || kpi.blocked || 0
                    });
                }
            }
        } catch (err) {
            console.error("Failed to fetch ads:", err);
            toast.error("Failed to load advertisements");
        } finally {
            setLoading(false);
        }
    }, [currentPage, searchTerm, statusFilter, activeTab]);

    const fetchRejectedAds = useCallback(async () => {
        if (activeTab === "ALL") return;
        setLoading(true);
        try {
            const params = {
                page: rejectedPage,
                size: 10,
                search: searchTerm || undefined
            };
            const response = await AdminService.getRejectedAds(params);

            if (response.data?.success) {
                const { content, totalPages, totalElements, kpi } = response.data.data;
                setRejectedAds(content || []);
                setRejectedTotalPages(totalPages || 0);
                setRejectedTotalItems(totalElements || 0);
                if (kpi) {
                    setStats({
                        total: kpi.totalAds || kpi.total || 0,
                        active: kpi.liveAds || kpi.active || 0,
                        pending: kpi.pendingReview || kpi.pending || 0,
                        blocked: kpi.rejectedAds || kpi.blocked || 0
                    });
                }
            }
        } catch (err) {
            console.error("Failed to fetch rejected ads:", err);
            toast.error("Failed to load rejected list");
        } finally {
            setLoading(false);
        }
    }, [rejectedPage, searchTerm, activeTab]);

    useEffect(() => {
        if (activeTab === "ALL") fetchAds();
        else fetchRejectedAds();
    }, [fetchAds, fetchRejectedAds, activeTab]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (activeTab === "ALL") setCurrentPage(0);
            else setRejectedPage(0);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm, activeTab]);

    const handleAccept = async () => {
        if (!selectedAd || !selectedAd.id) {
            toast.error("Invalid selection. Ad ID missing.");
            return;
        }
        setIsProcessing(true);
        try {
            if (activeTab === "REJECTED" && !actionComment.trim()) {
                toast.error("Approval reason is required for re-accepting");
                setIsProcessing(false);
                return;
            }
            await AdminService.acceptAd(selectedAd.id, actionComment);
            toast.success("Advertisement accepted successfully");
            if (activeTab === "ALL") fetchAds();
            else fetchRejectedAds();
            closeModals();
        } catch (err) {
            toast.error("Failed to accept advertisement");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleReject = async () => {
        if (!selectedAd || !selectedAd.id) {
            toast.error("Invalid selection. Ad ID missing.");
            return;
        }
        if (!actionComment.trim()) {
            toast.error("Reason for rejection is mandatory");
            return;
        }
        setIsProcessing(true);
        try {
            await AdminService.rejectAd(selectedAd.id, actionComment);
            toast.success("Advertisement rejected and added to rejected list");
            fetchAds();
            closeModals();
        } catch (err) {
            toast.error("Failed to reject advertisement");
        } finally {
            setIsProcessing(false);
        }
    };

    const handleDelete = async (ad) => {
        if (window.confirm(`Are you sure you want to permanently delete "${ad.title}"?`)) {
            try {
                await AdminService.deleteAd(ad.id);
                toast.success("Ad deleted successfully");
                if (activeTab === "ALL") fetchAds();
                else fetchRejectedAds();
            } catch (err) {
                toast.error("Failed to delete ad");
            }
        }
    };

    const closeModals = () => {
        setShowRejectModal(false);
        setShowAcceptModal(false);
        setSelectedAd(null);
        setActionComment("");
    };

    return (
        <div className="admin-ads-container">
            <div className="admin-header-block">
                <h1 className="admin-large-title">Ads Management</h1>
                <div className="admin-tabs-compact">
                    <button
                        className={`admin-tab-pill ${activeTab === 'ALL' ? 'active' : ''}`}
                        onClick={() => setActiveTab('ALL')}
                    >
                        All Ads
                    </button>
                    <button
                        className={`admin-tab-pill ${activeTab === 'REJECTED' ? 'active' : ''}`}
                        onClick={() => setActiveTab('REJECTED')}
                    >
                        Rejected Ads
                    </button>
                </div>
            </div>

            {/* Stats Overview */}
            <div className="admin-stats-grid">
                <div className="stats-card">
                    <div className="stats-icon-box bg-blue">
                        <FiLayers size={18} />
                    </div>
                    <div className="stats-info">
                        <span className="stats-value">{stats.total || 0}</span>
                        <span className="stats-label">Total Ads</span>
                    </div>
                </div>
                <div className="stats-card">
                    <div className="stats-icon-box bg-green">
                        <FiTrendingUp size={18} />
                    </div>
                    <div className="stats-info">
                        <span className="stats-value">{stats.active || 0}</span>
                        <span className="stats-label">Live Ads</span>
                    </div>
                </div>
                <div className="stats-card">
                    <div className="stats-icon-box bg-amber">
                        <FiClock size={18} />
                    </div>
                    <div className="stats-info">
                        <span className="stats-value">{stats.pending || 0}</span>
                        <span className="stats-label">Pending Review</span>
                    </div>
                </div>
                <div className="stats-card">
                    <div className="stats-icon-box bg-rose">
                        <FiX size={18} />
                    </div>
                    <div className="stats-info">
                        <span className="stats-value">{stats.blocked || 0}</span>
                        <span className="stats-label">Rejected Ads</span>
                    </div>
                </div>
            </div>

            <div className="premium-card">
                <div className="filters-header">
                    <div className="search-field">
                        <FiSearch className="search-icon" size={20} />
                        <input
                            type="text"
                            placeholder="Search by title, ID or user..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {activeTab === "ALL" && (
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <select
                                className="pag-btn"
                                style={{ padding: '8px 12px' }}
                                value={statusFilter}
                                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(0); }}
                            >
                                {STATUS_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                            <button className="pag-btn" onClick={() => toast.info("Exporting...")}><FiDownload size={18} /> Export</button>
                        </div>
                    )}
                </div>

                <div style={{ minHeight: '400px' }}>
                    {loading ? (
                        Array(5).fill(0).map((_, i) => <SkeletonRow key={i} />)
                    ) : (
                        activeTab === "ALL" ? (
                            <>
                                <table className="premium-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '70px' }}>Icon</th>
                                            <th><FiInfo size={14} style={{ marginRight: '6px' }} />Ad Details</th>
                                            <th><FiUser size={14} style={{ marginRight: '6px' }} />Posted By</th>
                                            <th><FiLayers size={14} style={{ marginRight: '6px' }} />Category</th>
                                            <th>Price</th>
                                            <th>Status</th>
                                            <th style={{ textAlign: 'right' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            Array(5).fill(0).map((_, i) => (
                                                <tr key={i}><td colSpan="7"><SkeletonRow /></td></tr>
                                            ))
                                        ) : ads.length > 0 ? ads.map(ad => (
                                            <tr key={ad.id}>
                                                <td>
                                                    <div className="ad-image-wrapper">
                                                        <img
                                                            src={getImageUrl(ad.thumbnail)}
                                                            alt=""
                                                            onError={(e) => { e.target.src = "https://via.placeholder.com/50"; }}
                                                        />
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="ad-text-info">
                                                        <span className="ad-id">ID: #{ad.id}</span>
                                                        <a href="#" className="ad-title-link" onClick={(e) => { e.preventDefault(); navigate(`/admin/ads/view/${ad.id}`); }}>{ad.title}</a>
                                                        <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                                                            <span style={{ fontSize: '11px', color: '#94a3b8' }} title="Created On"><FiClock size={12} /> {formatDate(ad.createdAt)}</span>
                                                            <span
                                                                style={{ fontSize: '11px', color: isExpired(ad.expireDate) ? '#ef4444' : '#f59e0b' }}
                                                                title="Expires On"
                                                            >
                                                                <FiAlertCircle size={12} /> {formatDate(ad.expireDate)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                        <span style={{ fontSize: '13px', fontWeight: 500 }}><FiUser size={14} style={{ marginRight: '4px' }} />{ad.userName}</span>
                                                        <span style={{ fontSize: '11px', color: '#94a3b8' }}>ID: #{ad.userId}</span>
                                                    </div>
                                                </td>
                                                <td><span style={{ fontSize: '13px', color: '#64748b' }}><FiLayers size={14} style={{ marginRight: '4px' }} />{ad.categoryName || 'N/A'}</span></td>
                                                <td><span style={{ fontWeight: 600, color: '#1e293b', fontSize: '14px' }}>${ad.price?.toLocaleString()}</span></td>
                                                <td>{getStatusBadge(ad.status)}</td>
                                                <td>
                                                    <div className="action-btn-group">
                                                        <button className="pag-btn" style={{ padding: '4px 6px' }} onClick={() => navigate(`/admin/ads/view/${ad.id}`)} title="View Detail"><FiEye size={18} /></button>
                                                        <button className="pag-btn" style={{ padding: '4px 6px' }} onClick={() => navigate(`/admin/ads/edit/${ad.id}`)} title="Edit Ad"><FiEdit2 size={18} /></button>
                                                        {ad.status === "PENDING" && (
                                                            <button className="pag-btn" style={{ padding: '4px 6px', color: '#15803d' }} onClick={() => { setSelectedAd(ad); setShowAcceptModal(true); }} title="Approve Ad"><FiCheckCircle size={18} /></button>
                                                        )}
                                                        <button className="pag-btn" style={{ padding: '4px 6px', color: '#b91c1c' }} onClick={() => { setSelectedAd(ad); setShowRejectModal(true); }} title="Reject Ad"><FiXCircle size={18} /></button>
                                                        <button className="pag-btn" style={{ padding: '4px 6px' }} onClick={() => handleDelete(ad)} title="Delete Ad"><FiTrash2 size={18} /></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr><td colSpan="6" className="empty-state-container"><FiAlertCircle className="empty-state-icon" /><p>No matching advertisements found</p></td></tr>
                                        )}
                                    </tbody>
                                </table>
                                <div className="pagination-container">
                                    <span style={{ fontSize: '13px', color: '#64748b' }}>Showing {currentPage * 10 + 1}-{Math.min((currentPage + 1) * 10, totalItems)} of {totalItems}</span>
                                    <AdvancedPagination current={currentPage} total={totalPages} onPageChange={setCurrentPage} />
                                </div>
                            </>
                        ) : (
                            <>
                                <table className="premium-table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '70px' }}>Icon</th>
                                            <th><FiInfo size={14} style={{ marginRight: '6px' }} />Ad Details</th>
                                            <th><FiUser size={14} style={{ marginRight: '6px' }} />Posted By</th>
                                            <th><FiLayers size={14} style={{ marginRight: '6px' }} />Category</th>
                                            <th>Price</th>
                                            <th>Rejection Reason</th>
                                            <th style={{ textAlign: 'right' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            Array(5).fill(0).map((_, i) => (
                                                <tr key={i}><td colSpan="7"><SkeletonRow /></td></tr>
                                            ))
                                        ) : rejectedAds.length > 0 ? rejectedAds.map(ad => (
                                            <tr key={ad.id}>
                                                <td>
                                                    <div className="ad-image-wrapper">
                                                        <img
                                                            src={getImageUrl(ad.thumbnail)}
                                                            alt=""
                                                            onError={(e) => { e.target.src = "https://via.placeholder.com/50"; }}
                                                        />
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="ad-text-info">
                                                        <span className="ad-id">ID: #{ad.id}</span>
                                                        <a href="#" className="ad-title-link" onClick={(e) => { e.preventDefault(); navigate(`/admin/ads/view/${ad.id}`); }}>{ad.title}</a>
                                                        <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                                                            <span style={{ fontSize: '11px', color: '#94a3b8' }} title="Created On"><FiClock size={12} /> {formatDate(ad.createdAt)}</span>
                                                            <span
                                                                style={{ fontSize: '11px', color: isExpired(ad.expireDate) ? '#ef4444' : '#f59e0b' }}
                                                                title="Expires On"
                                                            >
                                                                <FiAlertCircle size={12} /> {formatDate(ad.expireDate)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                        <span style={{ fontSize: '13px', fontWeight: 500 }}><FiUser size={14} style={{ marginRight: '4px' }} />{ad.userName}</span>
                                                        <span style={{ fontSize: '11px', color: '#94a3b8' }}>ID: #{ad.userId}</span>
                                                    </div>
                                                </td>
                                                <td><span style={{ fontSize: '13px', color: '#64748b' }}><FiLayers size={14} style={{ marginRight: '4px' }} />{ad.categoryName || 'N/A'}</span></td>
                                                <td><span style={{ fontWeight: 600, color: '#1e293b', fontSize: '14px' }}>${ad.price?.toLocaleString()}</span></td>
                                                <td>
                                                    <div className="truncate-text" title={ad.rejectionComment}>
                                                        <FiMessageSquare size={16} style={{ marginRight: '4px', verticalAlign: 'middle', color: '#ef4444' }} />
                                                        <span style={{ fontSize: '12px', color: '#b91c1c', fontWeight: 500 }}>{ad.rejectionComment || "No reason provided"}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="action-btn-group">
                                                        <button className="pag-btn" style={{ padding: '6px' }} onClick={() => navigate(`/admin/ads/view/${ad.id}`)} title="View Detail"><FiEye size={20} /></button>
                                                        <button className="pag-btn" style={{ padding: '6px' }} onClick={() => navigate(`/admin/ads/edit/${ad.id}`)} title="Edit Ad"><FiEdit2 size={20} /></button>
                                                        <button className="pag-btn" style={{ padding: '6px', color: '#15803d' }} onClick={() => { setSelectedAd(ad); setShowAcceptModal(true); }} title="Re-Approve"><FiCheckCircle size={20} /></button>
                                                        <button className="pag-btn" style={{ padding: '6px', color: '#b91c1c' }} onClick={() => handleDelete(ad)} title="Delete Ad"><FiTrash2 size={20} /></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr><td colSpan="6" className="empty-state-container"><FiAlertCircle className="empty-state-icon" /><p>Your rejected list is currently empty</p></td></tr>
                                        )}
                                    </tbody>
                                </table>
                                <div className="pagination-container">
                                    <span style={{ fontSize: '13px', color: '#64748b' }}>Showing {rejectedPage * 10 + 1}-{Math.min((rejectedPage + 1) * 10, rejectedTotalItems)} of {rejectedTotalItems}</span>
                                    <AdvancedPagination current={rejectedPage} total={rejectedTotalPages} onPageChange={setRejectedPage} />
                                </div>
                            </>
                        )
                    )}
                </div>
            </div>

            {/* Modals */}
            {(showRejectModal || showAcceptModal) && (
                <div className="modal-overlay">
                    <div className={`premium-modal ${showRejectModal ? 'modal-md' : 'modal-sm'}`}>
                        <div className="modal-header">
                            <h2>{showRejectModal ? 'Reject Advertisement' : 'Accept Advertisement'}</h2>
                            <button className="close-btn" onClick={closeModals}><FiXCircle /></button>
                        </div>
                        <div className="modal-body">
                            <div className="modal-ad-preview" style={{ marginBottom: '16px', background: '#f8fafc', padding: '12px', borderRadius: '10px' }}>
                                <span style={{ fontSize: '11px', color: '#94a3b8', display: 'block' }}>Advertisement Info:</span>
                                <strong style={{ fontSize: '14px', color: '#1e293b' }}>#{selectedAd?.id} - {selectedAd?.title}</strong>
                            </div>

                            {showRejectModal ? (
                                <div className="form-group">
                                    <label style={{ fontSize: '13px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '8px' }}>
                                        Reason for Rejection <span style={{ color: '#ef4444' }}>*</span>
                                    </label>
                                    <textarea
                                        className="form-control"
                                        style={{ width: '100%', borderRadius: '10px', padding: '12px', border: '1px solid #e2e8f0', minHeight: '120px', outline: 'none' }}
                                        placeholder="Explain why this ad is being rejected..."
                                        value={actionComment}
                                        onChange={(e) => setActionComment(e.target.value)}
                                        autoFocus
                                    ></textarea>
                                    <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px' }}>This reason will be visible to the user who posted the ad.</p>
                                </div>
                            ) : (
                                <div className="form-group">
                                    <p style={{ fontSize: '14px', color: '#475569', marginBottom: '16px' }}>
                                        Are you sure you want to approve this advertisement? It will be live and visible to all platform users.
                                    </p>
                                    <label style={{ fontSize: '13px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '8px' }}>
                                        {activeTab === 'REJECTED' ? 'Reason for Re-approval *' : 'Internal Note (Optional)'}
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        style={{ width: '100%', borderRadius: '10px', padding: '10px 14px', border: '1px solid #e2e8f0', outline: 'none' }}
                                        placeholder={activeTab === 'REJECTED' ? 'Why is this being re-approved?' : 'Add an internal note...'}
                                        value={actionComment}
                                        onChange={(e) => setActionComment(e.target.value)}
                                        autoFocus
                                    />
                                </div>
                            )}
                        </div>
                        <div className="modal-footer" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', padding: '20px', background: '#f8fafc' }}>
                            <button className="btn-secondary" style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer' }} onClick={closeModals} disabled={isProcessing}>Cancel</button>
                            <button
                                className={showRejectModal ? 'btn-danger' : 'btn-success'}
                                style={{ padding: '10px 24px', borderRadius: '8px', border: 'none', background: showRejectModal ? '#b91c1c' : '#15803d', color: '#fff', fontWeight: 600, cursor: 'pointer', opacity: (showRejectModal && !actionComment.trim()) ? 0.6 : 1 }}
                                onClick={showRejectModal ? handleReject : handleAccept}
                                disabled={isProcessing || (showRejectModal && !actionComment.trim()) || (activeTab === 'REJECTED' && !showRejectModal && !actionComment.trim())}
                            >
                                {isProcessing ? "Processing..." : (showRejectModal ? "Confirm Rejection" : "Confirm Acceptance")}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .modal-overlay {
                    position: fixed;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(15, 23, 42, 0.6);
                    backdrop-filter: blur(4px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                    animation: fadeInOverlay 0.2s ease-out;
                }
                .premium-modal {
                    background: #fff;
                    border-radius: 16px;
                    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
                    overflow: hidden;
                    width: 90%;
                    max-width: 450px;
                    animation: slideUpModal 0.3s cubic-bezier(0.16, 1, 0.3, 1);
                }
                .modal-md { max-width: 500px; }
                .modal-sm { max-width: 400px; }
                .modal-header {
                    padding: 20px;
                    border-bottom: 1px solid #f1f5f9;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                .modal-header h2 { font-size: 18px; font-weight: 700; color: #1e293b; margin: 0; }
                .close-btn { background: none; border: none; font-size: 20px; color: #94a3b8; cursor: pointer; display: flex; align-items: center; }
                .modal-body { padding: 24px; }
                @keyframes fadeInOverlay { from { opacity: 0; } to { opacity: 1; } }
                @keyframes slideUpModal { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
};

export default AdListPage;
